import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/base/Seo";
import type { Category, DirectoryLocation } from "@/types/directory";
import type { Plan } from "@/types/plans";
import { getCategories, getLocations } from "@/services/directoryService";
import { getPlans } from "@/services/plansService";
import ListBusinessForm from "./components/ListBusinessForm";

const steps = [
  {
    icon: "ri-file-add-line",
    title: "Submit your details",
    description: "Fill in your business details — it takes about three minutes.",
  },
  {
    icon: "ri-shield-check-line",
    title: "We review & verify",
    description: "Our team checks your address, contact number and category.",
  },
  {
    icon: "ri-rocket-2-line",
    title: "Go live & get found",
    description: "Your profile goes live and starts reaching local customers.",
  },
];

const perks = [
  "Free forever basic listing",
  "Reach customers across three districts",
  "Direct calls, WhatsApp and enquiries",
  "Verified badge on paid plans",
];

export default function ListBusinessPage() {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<DirectoryLocation[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);

  const requestedPlan = searchParams.get("plan") ?? "free";
  const defaultPlan = ["free", "business", "premium"].includes(requestedPlan) ? requestedPlan : "free";

  useEffect(() => {
    let active = true;
    Promise.all([
      getCategories(),
      getLocations(),
      getPlans().catch(() => ({ plans: [], comparison: [], faqs: [] })),
    ])
      .then(([categoryList, locationList, planBundle]) => {
        if (!active) return;
        setCategories(categoryList);
        setLocations(locationList);
        setPlans(planBundle.plans);
      })
      .catch(() => {
        if (!active) return;
        setCategories([]);
        setLocations([]);
        setPlans([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const districts = locations.filter((location) => location.type === "district");

  return (
    <SiteLayout>
      <Seo
        title="List Your Business Free"
        description="Add your business to Coastal Bizz Connect for free. Reach customers searching across South Canara, Udupi and North Canara with a verified local listing, photos and direct enquiries."
        keywords="list your business Karnataka, add business Mangaluru, free business listing Udupi, register business North Canara"
        path="/list-your-business"
      />

      <section className="border-b border-background-200 bg-primary-900">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-12 md:py-16">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-accent-300">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-background-100">List your business</span>
          </nav>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-4xl">
            Get your business in front of the coast
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            Join thousands of local businesses already listed on Coastal Bizz Connect. Submit your
            details below and our team will take it from there.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <ListBusinessForm
              categories={categories.map((category) => ({ slug: category.slug, name: category.name }))}
              districts={districts.map((district) => ({ slug: district.slug, name: district.name }))}
              plans={plans}
              defaultPlan={defaultPlan}
            />
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                Why list with us
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                      <i className="ri-check-double-line text-base" />
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>
              <Link
                to="/listing-plans"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
              >
                Compare listing plans
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                What happens next
              </h2>
              <ol className="mt-4 flex flex-col gap-5">
                {steps.map((step, index) => (
                  <li key={step.title} className="flex gap-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                      <i className={`${step.icon} text-base`} />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-accent-700">
                        Step {index + 1}
                      </p>
                      <p className="mt-0.5 font-heading text-sm font-bold text-foreground-950">
                        {step.title}
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                        {step.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6">
              <p className="flex items-start gap-2.5 text-sm text-foreground-700">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                  <i className="ri-customer-service-2-line text-base" />
                </span>
                Need help? Email{" "}
                <a
                  href="mailto:info@coastalbizzconnect.com"
                  className="font-semibold text-secondary-800 underline-offset-2 hover:underline"
                >
                  info@coastalbizzconnect.com
                </a>
              </p>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}